import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Scale, Save, RefreshCw, TrendingUp, TrendingDown, Minus,
  AlertTriangle, CheckCircle2, XCircle, Sparkles, ArrowRight,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { GRADER } from "@/constants/testIds";

/* -------------------------------------------------------------------------- */
/*  Candle-pattern SVGs                                                       */
/* -------------------------------------------------------------------------- */
/*  Minimal, on-brand line drawings so students can see the shape of each     */
/*  candlestick pattern in the selector. Bullish is emerald, bearish rose.    */

function Candle({ x, w, top, bot, bodyTop, bodyBot, bull }) {
  const color = bull ? "#10b981" : "#ef4444";
  const fill = bull ? "#10b981" : "#ffffff";
  return (
    <g>
      <line x1={x + w / 2} x2={x + w / 2} y1={top} y2={bot} stroke={color} strokeWidth="1.2" />
      <rect x={x} y={Math.min(bodyTop, bodyBot)} width={w} height={Math.abs(bodyBot - bodyTop)}
            fill={bull ? fill : "#fff"} stroke={color} strokeWidth="1.4" />
    </g>
  );
}

const PATTERN_SVGS = {
  hammer: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <Candle x={26} w={8} top={10} bot={50} bodyTop={12} bodyBot={22} bull />
    </svg>
  ),
  shooting_star: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <Candle x={26} w={8} top={10} bot={50} bodyTop={38} bodyBot={48} bull={false} />
    </svg>
  ),
  pin_bar_bull: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <Candle x={26} w={8} top={20} bot={54} bodyTop={22} bodyBot={30} bull />
    </svg>
  ),
  pin_bar_bear: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <Candle x={26} w={8} top={6} bot={40} bodyTop={32} bodyBot={40} bull={false} />
    </svg>
  ),
  engulfing_bullish: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <Candle x={16} w={7} top={22} bot={40} bodyTop={26} bodyBot={36} bull={false} />
      <Candle x={30} w={11} top={12} bot={48} bodyTop={20} bodyBot={44} bull />
    </svg>
  ),
  engulfing_bearish: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <Candle x={16} w={7} top={22} bot={40} bodyTop={26} bodyBot={36} bull />
      <Candle x={30} w={11} top={12} bot={48} bodyTop={16} bodyBot={40} bull={false} />
    </svg>
  ),
  morning_star: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <Candle x={6} w={7} top={14} bot={42} bodyTop={16} bodyBot={38} bull={false} />
      <Candle x={22} w={6} top={38} bot={54} bodyTop={44} bodyBot={48} bull />
      <Candle x={38} w={7} top={14} bot={44} bodyTop={18} bodyBot={40} bull />
    </svg>
  ),
  evening_star: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <Candle x={6} w={7} top={14} bot={44} bodyTop={18} bodyBot={40} bull />
      <Candle x={22} w={6} top={8} bot={22} bodyTop={12} bodyBot={16} bull={false} />
      <Candle x={38} w={7} top={14} bot={44} bodyTop={16} bodyBot={38} bull={false} />
    </svg>
  ),
  doji: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <line x1={30} x2={30} y1={10} y2={50} stroke="#0f172a" strokeWidth="1.4" />
      <line x1={20} x2={40} y1={30} y2={30} stroke="#0f172a" strokeWidth="2.5" />
    </svg>
  ),
  inside_bar: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <Candle x={12} w={12} top={10} bot={50} bodyTop={14} bodyBot={46} bull={false} />
      <Candle x={34} w={9} top={20} bot={40} bodyTop={24} bodyBot={36} bull />
    </svg>
  ),
};

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const emptyForm = {
  instrument: "",
  direction: "long",
  setup_type: "pullback",
  bias: "uptrend",
  level: "support",
  fib_level: "none",
  candle_patterns: [],
  ma_position: "not_used",
  higher_tf_agrees: false,
  above_avg_volume: false,
  planned_rr: "",
  notes: "",
  entry_price: "",
  stop_price: "",
};

const GRADE_COLORS = {
  A: { bg: "bg-emerald-500", ring: "ring-emerald-500/40", text: "text-emerald-400", label: "Exceptional" },
  B: { bg: "bg-blue-500",    ring: "ring-blue-500/40",    text: "text-blue-400",    label: "Strong" },
  C: { bg: "bg-amber-500",   ring: "ring-amber-500/40",   text: "text-amber-400",   label: "Average" },
  D: { bg: "bg-orange-500",  ring: "ring-orange-500/40",  text: "text-orange-400",  label: "Weak" },
  F: { bg: "bg-rose-500",    ring: "ring-rose-500/40",    text: "text-rose-400",    label: "Poor" },
};

/* -------------------------------------------------------------------------- */
/*  Grader                                                                    */
/* -------------------------------------------------------------------------- */

export default function Grader() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const [form, setForm] = useState(emptyForm);
  const [schema, setSchema] = useState(null);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [climbPlanId, setClimbPlanId] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const togglePattern = (id) => {
    setForm((f) => {
      const has = f.candle_patterns.includes(id);
      return {
        ...f,
        candle_patterns: has
          ? f.candle_patterns.filter((p) => p !== id)
          : [...f.candle_patterns, id],
      };
    });
  };

  // Fetch schema + accounts + plans once
  useEffect(() => {
    Promise.all([
      api.get("/grader/schema", { params: { lang } }),
      api.get("/accounts").catch(() => ({ data: [] })),
      api.get("/climb/plans").catch(() => ({ data: [] })),
    ]).then(([s, a, p]) => {
      setSchema(s.data);
      setAccounts(a.data);
      setPlans(p.data);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Live score — debounced
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const payload = {
          ...form,
          planned_rr: form.planned_rr === "" ? null : parseFloat(form.planned_rr),
        };
        delete payload.entry_price;
        delete payload.stop_price;
        const r = await api.post("/grader/score", payload, { params: { lang } });
        setResult(r.data);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, lang]);

  const grade = result?.grade || "C";
  const gradeStyle = GRADE_COLORS[grade];

  const filteredPlans = useMemo(() => {
    if (!accountId) return plans;
    return plans.filter((p) => !p.account_id || p.account_id === accountId);
  }, [plans, accountId]);

  const saveToJournal = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        planned_rr: form.planned_rr === "" ? null : parseFloat(form.planned_rr),
        entry_price: form.entry_price === "" ? null : parseFloat(form.entry_price),
        stop_price: form.stop_price === "" ? null : parseFloat(form.stop_price),
        account_id: accountId || null,
        climb_plan_id: climbPlanId || null,
      };
      const r = await api.post("/grader/save-to-journal", payload);
      toast.success(t("graderUi.savedSuccess", { grade: r.data.grade }));
      // Reset the form after save.
      setForm(emptyForm);
      setAccountId("");
      setClimbPlanId("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("graderUi.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setForm(emptyForm);
    setAccountId("");
    setClimbPlanId("");
  };

  if (!schema) {
    return <div className="text-slate-500 text-sm py-10 text-center">{t("graderUi2.loadingGrader")}</div>;
  }

  const activePatterns = form.candle_patterns;

  return (
    <div data-testid={GRADER.root} className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-6">
      {/* -------- Left: form -------- */}
      <div className="space-y-4">
        {/* Warning */}
        <div
          data-testid={GRADER.warning}
          className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3"
        >
          <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>{t("graderUi2.warningBold")}</strong> {t("graderUi2.warningText")}
          </p>
        </div>

        {/* Setup + bias card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-800 text-white flex items-center justify-center">
              <Scale size={18} />
            </div>
            <div>
              <h3 className="font-display font-bold text-lg text-slate-900">{t("graderUi2.gradeSetup")}</h3>
              <p className="text-xs text-slate-500">{t("graderUi2.gradeSetupDesc")}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.instrument")}</label>
              <Input
                data-testid={GRADER.instrument}
                value={form.instrument}
                onChange={(e) => set("instrument", e.target.value)}
                placeholder={t("graderUi2.instrumentPlaceholder")}
                className="mt-1 h-10 bg-white"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.direction")}</label>
              <div data-testid={GRADER.direction} className="mt-1 flex rounded-md bg-slate-100 p-0.5 h-10">
                {[
                  ["long",  t("graderUi2.long"),  TrendingUp],
                  ["short", t("graderUi2.short"), TrendingDown],
                ].map(([v, l, Icon]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set("direction", v)}
                    className={`flex-1 text-xs font-bold rounded transition-colors inline-flex items-center justify-center gap-1.5 ${
                      form.direction === v
                        ? (v === "long" ? "bg-emerald-500 text-white" : "bg-rose-500 text-white")
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    <Icon size={12} /> {l}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.setupType")}</label>
              <select
                data-testid={GRADER.setupType}
                value={form.setup_type}
                onChange={(e) => set("setup_type", e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
              >
                {schema.setup_types.map((s) => (
                  <option key={s.id} value={s.id}>{s.label} (+{s.points})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.bias")}</label>
              <div data-testid={GRADER.bias} className="mt-1 flex rounded-md bg-slate-100 p-0.5 h-10">
                {[
                  ["uptrend",       TrendingUp,   "text-emerald-600"],
                  ["consolidation", Minus,        "text-slate-600"],
                  ["downtrend",     TrendingDown, "text-rose-600"],
                ].map(([v, Icon, iconColor]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set("bias", v)}
                    title={schema.bias_labels?.[v] || v}
                    aria-label={schema.bias_labels?.[v] || v}
                    className={`${form.bias === v ? "flex-[2]" : "flex-1"} min-w-0 truncate px-1 text-[11px] font-bold rounded transition-colors inline-flex items-center justify-center gap-1 ${
                      form.bias === v
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    <Icon size={12} className={form.bias === v ? iconColor : ""} />
                    {form.bias === v && <span className="truncate">{schema.bias_labels?.[v] || v}</span>}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.keyLevel")}</label>
              <select
                data-testid={GRADER.level}
                value={form.level}
                onChange={(e) => set("level", e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
              >
                {schema.levels.map((l) => (
                  <option key={l.id} value={l.id}>{l.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.fibRetracement")}</label>
              <select
                data-testid={GRADER.fib}
                value={form.fib_level}
                onChange={(e) => set("fib_level", e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
              >
                <option value="none">{t("graderUi2.none")}</option>
                {schema.fib_levels.filter((f) => f.id !== "none").map((f) => (
                  <option key={f.id} value={f.id}>{f.id} (+{f.points})</option>
                ))}
              </select>
            </div>
          </div>

          {/* Candle patterns */}
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
              {t("graderUi2.candlePatterns")}
            </label>
            <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2">
              {schema.candle_patterns.map((p) => {
                const active = activePatterns.includes(p.id);
                const svg = PATTERN_SVGS[p.id];
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`${GRADER.pattern}-${p.id}`}
                    onClick={() => togglePattern(p.id)}
                    className={`p-2 rounded-lg border-2 transition-all text-left ${
                      active
                        ? "bg-blue-50 border-blue-500 shadow-sm"
                        : "bg-white border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="h-14 flex items-center justify-center">
                      {svg || <div className="text-[10px] text-slate-400">no svg</div>}
                    </div>
                    <div className="text-[10px] font-semibold text-slate-700 leading-tight mt-1">
                      {p.label}
                    </div>
                    <div className="text-[9px] font-mono text-amber-600 mt-0.5">+{p.points}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Moving averages + volume + rr */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.maPosition")}</label>
              <select
                data-testid={GRADER.maPosition}
                value={form.ma_position}
                onChange={(e) => set("ma_position", e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
              >
                {schema.ma_positions.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.plannedRR")}</label>
              <div className="relative">
                <Input
                  data-testid={GRADER.rr}
                  type="number"
                  inputMode="decimal"
                  step="any"
                  value={form.planned_rr}
                  onChange={(e) => set("planned_rr", e.target.value)}
                  placeholder={t("graderUi2.rrPlaceholder")}
                  className="mt-1 h-10 pr-8 bg-white"
                />
                <span className="absolute right-3 top-1/2 translate-y-0.5 text-xs font-mono text-slate-400">R</span>
              </div>
            </div>

            <label className="col-span-2 sm:col-span-2 flex items-center gap-2 h-10 mt-6 cursor-pointer">
              <input
                data-testid={GRADER.higherTf}
                type="checkbox"
                checked={form.higher_tf_agrees}
                onChange={(e) => set("higher_tf_agrees", e.target.checked)}
                className="w-4 h-4 accent-blue-800"
              />
              <span className="text-sm text-slate-700">{t("graderUi2.higherTfAgrees")} <span className="text-amber-600 font-mono text-xs">+6</span></span>
            </label>

            <label className="col-span-2 sm:col-span-2 flex items-center gap-2 h-10 mt-6 cursor-pointer">
              <input
                data-testid={GRADER.volume}
                type="checkbox"
                checked={form.above_avg_volume}
                onChange={(e) => set("above_avg_volume", e.target.checked)}
                className="w-4 h-4 accent-blue-800"
              />
              <span className="text-sm text-slate-700">{t("graderUi2.aboveAvgVolume")} <span className="text-amber-600 font-mono text-xs">+5</span></span>
            </label>
          </div>

          {/* Journal fields */}
          <div className="border-t border-slate-100 pt-5">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-3">
              {t("graderUi2.journalDefaults")}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.entry")}</label>
                <Input type="number" inputMode="decimal" step="any" value={form.entry_price} onChange={(e) => set("entry_price", e.target.value)} className="mt-1 h-10 bg-white" placeholder={t("graderUi2.optional")} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.stop")}</label>
                <Input type="number" inputMode="decimal" step="any" value={form.stop_price} onChange={(e) => set("stop_price", e.target.value)} className="mt-1 h-10 bg-white" placeholder={t("graderUi2.optional")} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.account")}</label>
                <select
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
                >
                  <option value="">{t("graderUi.noneOption")}</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.nickname}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.climbPlan")}</label>
                <select
                  value={climbPlanId}
                  onChange={(e) => setClimbPlanId(e.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
                >
                  <option value="">{t("graderUi.noneOption")}</option>
                  {filteredPlans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("graderUi2.notes")}</label>
              <Textarea
                data-testid={GRADER.notes}
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={2}
                className="mt-1 bg-white"
                placeholder={t("graderUi2.notesPlaceholder")}
              />
            </div>
          </div>
        </div>
      </div>

      {/* -------- Right: score panel (sticky on desktop) -------- */}
      <div className="xl:sticky xl:top-6 xl:self-start space-y-4">
        <div
          data-testid={GRADER.scoreCard}
          className="relative overflow-hidden rounded-2xl bg-slate-950 text-white p-6 border border-slate-800"
        >
          <div className={`absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl pointer-events-none ${gradeStyle.bg} opacity-20`} />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 font-semibold flex items-center gap-2">
              <Sparkles size={12} /> {t("graderUi2.liveGrade")}
            </div>

            <div className="mt-5 flex items-center gap-5">
              <div
                data-testid={GRADER.scoreGrade}
                className={`w-24 h-24 rounded-2xl flex items-center justify-center font-display font-black text-6xl text-white ${gradeStyle.bg} ring-4 ${gradeStyle.ring} shadow-lg`}
              >
                {grade}
              </div>
              <div>
                <div className={`text-xs uppercase tracking-widest font-semibold ${gradeStyle.text}`}>
                  {t(`graderUi2.grade${gradeStyle.label}`, gradeStyle.label)}
                </div>
                <div data-testid={GRADER.scoreNumber} className="font-mono font-black text-3xl text-white mt-1 tabular-nums">
                  {result?.score ?? 50}
                  <span className="text-slate-500 text-lg font-normal">/100</span>
                </div>
              </div>
            </div>

            {result?.verdict && (
              <p className="mt-4 text-sm text-slate-300 leading-relaxed">
                {result.verdict}
              </p>
            )}
          </div>
        </div>

        {/* Breakdown */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-3">
            {t("graderUi2.confluenceBreakdown")}
          </div>
          {result?.breakdown?.length ? (
            <ul className="space-y-2">
              {result.breakdown.map((b, i) => (
                <li key={i} data-testid={GRADER.breakdownRow} className="flex items-start gap-3">
                  {b.points > 0 ? (
                    <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                  ) : b.points < 0 ? (
                    <XCircle size={14} className="text-rose-500 mt-0.5 shrink-0" />
                  ) : (
                    <Minus size={14} className="text-slate-400 mt-0.5 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">{b.category}</div>
                    <div className="text-sm text-slate-800">{b.label}</div>
                  </div>
                  <div className={`font-mono font-bold text-sm tabular-nums shrink-0 ${
                    b.points > 0 ? "text-emerald-600" : b.points < 0 ? "text-rose-600" : "text-slate-500"
                  }`}>
                    {b.points > 0 ? "+" : ""}{b.points}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-500">{t("graderUi2.answerChecklist")}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            data-testid={GRADER.reset}
            variant="outline"
            onClick={reset}
            className="border-slate-300 h-11"
          >
            <RefreshCw size={14} className="mr-2" /> {t("graderUi2.reset")}
          </Button>
          <Button
            data-testid={GRADER.saveToJournal}
            onClick={saveToJournal}
            disabled={saving}
            className="flex-1 bg-blue-900 hover:bg-blue-800 text-white h-11 disabled:opacity-60"
          >
            <Save size={14} className="mr-2" />
            {saving ? t("graderUi2.saving") : t("graderUi2.saveToJournal")} <ArrowRight size={14} className="ml-2" />
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 text-center leading-relaxed px-2">
          {t("graderUi2.savingDescription")}
        </p>
      </div>
    </div>
  );
}
